/**
 * Chart Data Transformation Utilities
 * Converts Django API chart data to Recharts-compatible format
 */

import type { ChartData, DashboardMetric } from '../types/dashboard.types';

export interface RechartsDataPoint {
  name: string;           // X-axis label (date/period)
  [metricKey: string]: string | number;  // Dynamic metric values
}

export interface MetricConfig {
  key: string;
  label: string;
  color: string;
  stackId: string;
}

/**
 * Transform Django ChartData to Recharts format
 * Converts metrics with time-series data to stacked bar chart format
 */
export function transformChartDataForRecharts(chartData: ChartData): {
  data: RechartsDataPoint[];
  metrics: MetricConfig[];
  xAxisLabel: string;
  utcOffset: number;
} {
  const { x_axis_label, utc_offset, ...metricsData } = chartData;
  
  // Extract and process metrics
  const metricsMap: Record<string, DashboardMetric> = {};
  const metricsWithTotals: Array<{ key: string; metric: DashboardMetric; total: number }> = [];
  
  // Process each metric and calculate totals
  Object.entries(metricsData).forEach(([key, value]) => {
    if (typeof value === 'object' && value !== null && 'metric_values' in value) {
      const metric = value as DashboardMetric;
      metricsMap[key] = metric;
      
      // Calculate total for this metric
      const total = metric.metric_values.reduce((sum, point) => sum + point.value, 0);
      metricsWithTotals.push({ key, metric, total });
    }
  });
  
  // Sort metrics by total value (largest to smallest) for stacking order
  metricsWithTotals.sort((a, b) => b.total - a.total);
  
  // Create metric configurations in sorted order
  const metrics: MetricConfig[] = metricsWithTotals.map(({ key, metric }, index) => ({
    key,
    label: formatMetricLabel(key),
    color: metric.color || getDefaultColor(index),
    stackId: 'encounters' // Stack all metrics together
  }));
  
  // Find the maximum number of data points across all metrics
  const maxDataPoints = Math.max(
    ...Object.values(metricsMap).map(metric => metric.metric_values.length)
  );
  
  // Get the labels from the first available metric (they should all have the same labels)
  const firstMetric = Object.values(metricsMap)[0];
  const dateLabels = firstMetric?.metric_values.map(point => point.label) || [];
  
  // Transform to Recharts format
  const data: RechartsDataPoint[] = [];
  
  for (let i = 0; i < maxDataPoints; i++) {
    const dataPoint: RechartsDataPoint = {
      name: dateLabels[i] || `Period ${i + 1}` // Use Django-provided label or fallback
    };
    
    // Add each metric's value for this time period
    metrics.forEach(({ key }) => {
      const metric = metricsMap[key];
      const value = metric.metric_values[i]?.value || 0;
      dataPoint[key] = value;
    });
    
    data.push(dataPoint);
  }
  
  return {
    data,
    metrics,
    xAxisLabel: x_axis_label,
    utcOffset: utc_offset
  };
}

/**
 * Format metric key into human-readable label
 */
function formatMetricLabel(key: string): string {
  return key
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}


/**
 * Healthcare-appropriate color palette
 * Professional, accessible colors for medical environments
 */
function getDefaultColor(index: number): string {
  const healthcareColors = [
    '#2E86AB', // Medical Blue
    '#A23B72', // Healthcare Magenta
    '#F18F01', // Medical Orange
    '#C73E1D', // Emergency Red
    '#59A96A', // Health Green
    '#7209B7', // Clinical Purple
    '#F77F00', // Urgent Orange
    '#003566', // Professional Navy
  ];
  
  return healthcareColors[index % healthcareColors.length];
}

/**
 * Create chart click data for navigation
 */
export interface ChartClickData {
  metricKey: string;
  metricLabel: string;
  value: number;
  periodLabel: string;
  periodStart: string; // YYYY-MM-DD format
  periodEnd: string;   // YYYY-MM-DD format
  facilityId: number | null;
  xAxisLabel: string;  // 'Day' | 'Week' | 'Month' | 'Year'
}

/**
 * Parse Django date label to get date range for the period
 */
function parsePeriodDateRange(periodLabel: string, xAxisLabel: string): { start: string; end: string } {
  try {
    if (xAxisLabel === 'Day') {
      // Try multiple day formats: "Mon 08/25/25", "08/25/25", "Jun 25", "25"
      
      // Format: "Mon 08/25/25" or "08/25/25"
      let dateMatch = periodLabel.match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
      if (dateMatch) {
        const [, month, day, year] = dateMatch;
        const fullYear = year.length === 2 ? (parseInt(year) > 50 ? 1900 + parseInt(year) : 2000 + parseInt(year)) : parseInt(year);
        const date = `${fullYear}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        return { start: date, end: date };
      }
      
      // Format: "Jun 25" - assume current year
      dateMatch = periodLabel.match(/(\w{3})\s+(\d{1,2})/);
      if (dateMatch) {
        const [, monthName, day] = dateMatch;
        const currentYear = new Date().getFullYear();
        const monthNum = new Date(`${monthName} 1, ${currentYear}`).getMonth() + 1;
        const date = `${currentYear}-${monthNum.toString().padStart(2, '0')}-${day.padStart(2, '0')}`;
        return { start: date, end: date };
      }
      
    } else if (xAxisLabel === 'Week') {
      // Format: "Week 08/18/25-08/24/25" or "Jun 18-24"
      
      let weekMatch = periodLabel.match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})\s*-\s*(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
      if (weekMatch) {
        const [, startMonth, startDay, startYear, endMonth, endDay, endYear] = weekMatch;
        const startFullYear = startYear.length === 2 ? (parseInt(startYear) > 50 ? 1900 + parseInt(startYear) : 2000 + parseInt(startYear)) : parseInt(startYear);
        const endFullYear = endYear.length === 2 ? (parseInt(endYear) > 50 ? 1900 + parseInt(endYear) : 2000 + parseInt(endYear)) : parseInt(endYear);
        const start = `${startFullYear}-${startMonth.padStart(2, '0')}-${startDay.padStart(2, '0')}`;
        const end = `${endFullYear}-${endMonth.padStart(2, '0')}-${endDay.padStart(2, '0')}`;
        return { start, end };
      }
      
      // Format: "Jun 18-24" - assume current year
      weekMatch = periodLabel.match(/(\w{3})\s+(\d{1,2})-(\d{1,2})/);
      if (weekMatch) {
        const [, monthName, startDay, endDay] = weekMatch;
        const currentYear = new Date().getFullYear();
        const monthNum = new Date(`${monthName} 1, ${currentYear}`).getMonth() + 1;
        const start = `${currentYear}-${monthNum.toString().padStart(2, '0')}-${startDay.padStart(2, '0')}`;
        const end = `${currentYear}-${monthNum.toString().padStart(2, '0')}-${endDay.padStart(2, '0')}`;
        return { start, end };
      }
      
    } else if (xAxisLabel === 'Month') {
      // Format: "Aug 25", "Jun 2025", "06/2025"
      
      // Format: "Aug 25", "Jun 2025", or "Jun / 2025"
      let monthMatch = periodLabel.match(/(\w+)\s*\/\s*(\d{2,4})/);
      if (monthMatch) {
        const [, monthName, year] = monthMatch;
        const fullYear = year.length === 2 ? (parseInt(year) > 50 ? 1900 + parseInt(year) : 2000 + parseInt(year)) : parseInt(year);
        const monthNum = new Date(`${monthName} 1, ${fullYear}`).getMonth() + 1;
        const start = `${fullYear}-${monthNum.toString().padStart(2, '0')}-01`;
        const lastDay = new Date(fullYear, monthNum, 0).getDate();
        const end = `${fullYear}-${monthNum.toString().padStart(2, '0')}-${lastDay.toString().padStart(2, '0')}`;
        return { start, end };
      }
      
      // Format: "Aug 25" or "Jun 2025" (without slash)
      monthMatch = periodLabel.match(/(\w+)\s+(\d{2,4})/);
      if (monthMatch) {
        const [, monthName, year] = monthMatch;
        const fullYear = year.length === 2 ? (parseInt(year) > 50 ? 1900 + parseInt(year) : 2000 + parseInt(year)) : parseInt(year);
        const monthNum = new Date(`${monthName} 1, ${fullYear}`).getMonth() + 1;
        const start = `${fullYear}-${monthNum.toString().padStart(2, '0')}-01`;
        const lastDay = new Date(fullYear, monthNum, 0).getDate();
        const end = `${fullYear}-${monthNum.toString().padStart(2, '0')}-${lastDay.toString().padStart(2, '0')}`;
        return { start, end };
      }
      
      // Format: "06/2025" or "6/25"
      monthMatch = periodLabel.match(/(\d{1,2})\/(\d{2,4})/);
      if (monthMatch) {
        const [, month, year] = monthMatch;
        const fullYear = year.length === 2 ? (parseInt(year) > 50 ? 1900 + parseInt(year) : 2000 + parseInt(year)) : parseInt(year);
        const start = `${fullYear}-${month.padStart(2, '0')}-01`;
        const lastDay = new Date(fullYear, parseInt(month), 0).getDate();
        const end = `${fullYear}-${month.padStart(2, '0')}-${lastDay.toString().padStart(2, '0')}`;
        return { start, end };
      }
    }
  } catch (error) {
    console.warn('Error parsing period date range:', error);
  }
  
  console.warn('Could not parse period label:', periodLabel, 'for', xAxisLabel, 'falling back to today');
  // Fallback to today
  const today = new Date();
  const todayStr = today.toISOString().split('T')[0];
  return { start: todayStr, end: todayStr };
}

/**
 * Handle chart bar click and create navigation data
 * Note: userDateRange uses ISO strings for Redux serialization compatibility
 */
export function createChartClickData(
  data: any,
  metricKey: string,
  facilityId: number | null,
  userDateRange: { start: string; end: string },
  xAxisLabel: string
): ChartClickData {
  let { start, end } = parsePeriodDateRange(data.name, xAxisLabel);

  // CRITICAL: Clamp to user's selected date range (matching AngularJS behavior)
  // Parse ISO strings back to Date objects for comparison
  const userStartStr = new Date(userDateRange.start).toISOString().split('T')[0];
  const userEndStr = new Date(userDateRange.end).toISOString().split('T')[0];
  
  // If calculated period starts before user's range, clamp to user start
  if (start < userStartStr) {
    start = userStartStr;
  }
  
  // If calculated period ends after user's range, clamp to user end
  if (end > userEndStr) {
    end = userEndStr;
  }
  
  return {
    metricKey,
    metricLabel: formatMetricLabel(metricKey),
    value: data[metricKey] || 0,
    periodLabel: data.name,
    periodStart: start,
    periodEnd: end,
    facilityId,
    xAxisLabel
  };
}